import { useEffect } from "react";
import { useTranslation } from "react-i18next";

const originalTextNodes = new WeakMap<Text, string>();
const translatedAttributes = ["aria-label", "placeholder", "title"];

const isSkippableElement = (element: Element | null) => {
  if (!element) {
    return true;
  }

  const tagName = element.tagName.toLowerCase();
  return ["script", "style", "code", "pre", "textarea"].includes(tagName);
};

const normalizeText = (value: string) => value.replace(/\s+/g, " ").trim();

export function useDomLocalization() {
  const { i18n, t } = useTranslation();

  useEffect(() => {
    const translateTextNode = (node: Text) => {
      if (isSkippableElement(node.parentElement)) {
        return;
      }

      const original = originalTextNodes.get(node) ?? node.data;
      const normalized = normalizeText(original);

      if (normalized.length === 0 || /^[\d\s.,/:()-]+$/.test(normalized)) {
        return;
      }

      originalTextNodes.set(node, original);

      const translated = t(normalized, { defaultValue: normalized });
      node.data = original.replace(normalized, translated);
    };

    const translateElementAttributes = (element: Element) => {
      if (isSkippableElement(element)) {
        return;
      }

      translatedAttributes.forEach((attribute) => {
        const value = element.getAttribute(attribute);
        if (!value) {
          return;
        }

        const originalAttribute = `data-i18n-original-${attribute}`;
        const original =
          element.getAttribute(originalAttribute) ?? normalizeText(value);
        if (!element.hasAttribute(originalAttribute)) {
          element.setAttribute(originalAttribute, original);
        }

        const translated = t(original, { defaultValue: original });
        if (value !== translated) {
          element.setAttribute(attribute, translated);
        }
      });
    };

    const translateRoot = () => {
      const root = document.body;
      if (!root) {
        return;
      }

      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let node = walker.nextNode();

      while (node) {
        translateTextNode(node as Text);
        node = walker.nextNode();
      }

      root.querySelectorAll("*").forEach(translateElementAttributes);
      document.documentElement.lang = i18n.resolvedLanguage ?? i18n.language;
    };

    translateRoot();

    const observer = new MutationObserver((mutations) => {
      if (
        mutations.some(
          (mutation) =>
            mutation.type === "childList" || mutation.type === "attributes",
        )
      ) {
        window.requestAnimationFrame(translateRoot);
      }
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: translatedAttributes,
      childList: true,
      subtree: true,
    });

    i18n.on("languageChanged", translateRoot);

    return () => {
      observer.disconnect();
      i18n.off("languageChanged", translateRoot);
    };
  }, [i18n, t]);
}
